// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/ParametrosSistema.tsx
//  Parâmetros gerais do sistema: SMTP, flags fiscais e parâmetros operacionais.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Save, Mail, Flag, Settings2, RefreshCw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useConfigSistema } from '@/features/configuracoes/hooks/useConfiguracoes';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import type {
  AtualizarConfigSistemaRequest,
  ConfigSmtp,
  ConfigFlagsFiscais,
  ConfigOperacional,
} from '@/features/configuracoes/types';

// ── defaults ──────────────────────────────────────────────────────────────────

const defaultSmtp: ConfigSmtp = {
  servidorSmtp: null, portaSmtp: 587, usuarioSmtp: null,
  senhaSmtp: null, assuntoEmail: null, iSsl: false, iTls: true, iEmailHtml: true,
};

const defaultFlags: ConfigFlagsFiscais = {
  ibNfe: true, ibNfce: false, ibCte: false, ibMdfe: false, ibNfse: false,
};

const defaultOperacional: ConfigOperacional = {
  iDavBaixaEstoque: 0, iDavBaixaFinanceiro: 0,
  iPermVendaVariosDep: null, localPadraoVendaId: null,
  casasDecimaisQtde: 2, casasDecimaisValorUnit: 2,
  ibPermiteVendaEstoqueNegativo: false,
  condicionalExpiraAutomatico: false,
  apontamentoBloqueiaAcimaOrcado: false,
  versaoIbpt: null,
  bloqueiaEstoqueNegativo: false,
};

// ══════════════════════════════════════════════════════════════════════════════
//  Aba SMTP
// ══════════════════════════════════════════════════════════════════════════════

function TabSmtp({
  smtp,
  onChange,
}: {
  smtp: ConfigSmtp;
  onChange: (v: ConfigSmtp) => void;
}) {
  const set = <K extends keyof ConfigSmtp>(k: K, v: ConfigSmtp[K]) =>
    onChange({ ...smtp, [k]: v });

  return (
    <div className="space-y-6 pt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Servidor de Saída</CardTitle>
          <CardDescription>Utilizado para envio de DANFE e boletos por e-mail.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-1">
            <Label>Servidor SMTP</Label>
            <Input
              value={smtp.servidorSmtp ?? ''}
              onChange={(e) => set('servidorSmtp', e.target.value || null)}
              placeholder="smtp.gmail.com"
            />
          </div>
          <div className="space-y-1">
            <Label>Porta</Label>
            <Input
              type="number"
              value={smtp.portaSmtp ?? ''}
              onChange={(e) => set('portaSmtp', e.target.value ? Number(e.target.value) : null)}
              placeholder="587"
            />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label>Usuário / E-mail remetente</Label>
            <Input
              value={smtp.usuarioSmtp ?? ''}
              onChange={(e) => set('usuarioSmtp', e.target.value || null)}
              placeholder="nfe@empresa.com.br"
            />
          </div>
          <div className="space-y-1">
            <Label>Senha</Label>
            <Input
              type="password"
              value={smtp.senhaSmtp ?? ''}
              onChange={(e) => set('senhaSmtp', e.target.value || null)}
              placeholder="••••••••"
            />
          </div>
          <div className="md:col-span-3 space-y-1">
            <Label>Assunto padrão do e-mail</Label>
            <Input
              value={smtp.assuntoEmail ?? ''}
              onChange={(e) => set('assuntoEmail', e.target.value || null)}
              placeholder="NF-e — {empresa} — Nº {numero}"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Segurança e Formato</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">SSL</p>
              <p className="text-xs text-muted-foreground">Conexão segura SSL/TLS na porta 465.</p>
            </div>
            <Switch checked={smtp.iSsl} onCheckedChange={(v) => set('iSsl', v)} />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">STARTTLS</p>
              <p className="text-xs text-muted-foreground">Upgrade de conexão na porta 587 (recomendado).</p>
            </div>
            <Switch checked={smtp.iTls} onCheckedChange={(v) => set('iTls', v)} />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">E-mail em HTML</p>
              <p className="text-xs text-muted-foreground">Enviar mensagens formatadas em HTML.</p>
            </div>
            <Switch checked={smtp.iEmailHtml} onCheckedChange={(v) => set('iEmailHtml', v)} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Aba Flags Fiscais
// ══════════════════════════════════════════════════════════════════════════════

function TabFlags({
  flags,
  onChange,
}: {
  flags: ConfigFlagsFiscais;
  onChange: (v: ConfigFlagsFiscais) => void;
}) {
  const set = <K extends keyof ConfigFlagsFiscais>(k: K, v: boolean) =>
    onChange({ ...flags, [k]: v });

  const documentos: { key: keyof ConfigFlagsFiscais; label: string; desc: string }[] = [
    { key: 'ibNfe',  label: 'NF-e',   desc: 'Nota Fiscal Eletrônica (mod. 55)' },
    { key: 'ibNfce', label: 'NFC-e',  desc: 'Nota Fiscal de Consumidor (mod. 65)' },
    { key: 'ibCte',  label: 'CT-e',   desc: 'Conhecimento de Transporte Eletrônico' },
    { key: 'ibMdfe', label: 'MDF-e',  desc: 'Manifesto de Documentos Fiscais Eletrônicos' },
    { key: 'ibNfse', label: 'NFS-e',  desc: 'Nota Fiscal de Serviços Eletrônica' },
  ];

  return (
    <div className="space-y-4 pt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Documentos Fiscais Habilitados</CardTitle>
          <CardDescription>
            Ative apenas os documentos emitidos por esta empresa. Documentos desabilitados
            não aparecem nos menus.
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {documentos.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={flags[key]
                    ? 'border-green-500 text-green-600'
                    : 'text-muted-foreground'}
                >
                  {label}
                </Badge>
                <div>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
              </div>
              <Switch
                checked={flags[key]}
                onCheckedChange={(v) => set(key, v)}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Aba Operacional
// ══════════════════════════════════════════════════════════════════════════════

function TabOperacional({
  op,
  onChange,
}: {
  op: ConfigOperacional;
  onChange: (v: ConfigOperacional) => void;
}) {
  const set = <K extends keyof ConfigOperacional>(k: K, v: ConfigOperacional[K]) =>
    onChange({ ...op, [k]: v });

  const { data: locais = [], isLoading: carregandoLocais } = useLocalEstoqueCombo();
  const exigeLocal = op.iDavBaixaEstoque === 1;
  const localFaltando = exigeLocal && !op.localPadraoVendaId;

  return (
    <div className="space-y-6 pt-4">
      {/* DAV */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">DAV (Documento Auxiliar de Venda)</CardTitle>
          <CardDescription>Comportamento ao fechar/transformar um DAV em NF-e.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Baixa automática de estoque</Label>
            <Select
              value={String(op.iDavBaixaEstoque)}
              onValueChange={(v) => set('iDavBaixaEstoque', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Não baixar</SelectItem>
                <SelectItem value="1">Baixar ao fechar DAV</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Baixa automática financeira</Label>
            <Select
              value={String(op.iDavBaixaFinanceiro)}
              onValueChange={(v) => set('iDavBaixaFinanceiro', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Não baixar</SelectItem>
                <SelectItem value="1">Baixar ao fechar DAV</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1 md:col-span-2">
            <Label className={localFaltando ? 'text-destructive' : undefined}>
              Local padrão de venda {exigeLocal && <span className="text-destructive">*</span>}
            </Label>
            <Select
              value={op.localPadraoVendaId ? String(op.localPadraoVendaId) : ''}
              onValueChange={(v) => set('localPadraoVendaId', v ? Number(v) : null)}
              disabled={carregandoLocais}
            >
              <SelectTrigger>
                <SelectValue placeholder={carregandoLocais ? 'Carregando...' : 'Selecione um local'} />
              </SelectTrigger>
              <SelectContent>
                {locais.map((l) => (
                  <SelectItem key={l.id} value={String(l.id)}>
                    {l.descricao}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className={`text-xs ${localFaltando ? 'text-destructive' : 'text-muted-foreground'}`}>
              {localFaltando
                ? 'Obrigatório enquanto a baixa automática de estoque estiver ativa.'
                : 'Local de estoque utilizado para gerar os movimentos quando o DAV é baixado.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Vendas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Parâmetros de Venda</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Bloquear saída sem saldo em estoque</p>
              <p className="text-xs text-muted-foreground">
                Recusa venda, consumo de produção, requisição e transferência que deixem
                negativo o saldo de um produto que controla estoque.
              </p>
            </div>
            <Switch
              checked={op.bloqueiaEstoqueNegativo}
              onCheckedChange={(v) => set('bloqueiaEstoqueNegativo', v)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Permitir venda em vários departamentos</p>
              <p className="text-xs text-muted-foreground">
                Habilita seleção de múltiplos departamentos/seções em uma única venda.
              </p>
            </div>
            <Switch
              checked={!!op.iPermVendaVariosDep}
              onCheckedChange={(v) => set('iPermVendaVariosDep', v ? 1 : 0)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Expirar condicionais vencidas automaticamente</p>
              <p className="text-xs text-muted-foreground">
                Ao acionar "Expirar vencidas", devolve ao depósito o saldo de condicionais com validade
                vencida. Deixe desligado se a mercadoria costuma seguir com o cliente (acerto manual).
              </p>
            </div>
            <Switch
              checked={op.condicionalExpiraAutomatico}
              onCheckedChange={(v) => set('condicionalExpiraAutomatico', v)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Bloquear apontamento de horas acima do orçado</p>
              <p className="text-xs text-muted-foreground">
                Recusa a aprovação de timesheet quando o custo de mão de obra (horas aprovadas × custo-hora)
                ultrapassar o valor orçado do projeto. Deixe desligado para permitir apontamento retroativo
                sem teto. Projetos sem valor orçado nunca são bloqueados.
              </p>
            </div>
            <Switch
              checked={op.apontamentoBloqueiaAcimaOrcado}
              onCheckedChange={(v) => set('apontamentoBloqueiaAcimaOrcado', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Casas decimais e IBPT */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Precisão e IBPT</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Casas decimais — Valor Unitário</Label>
            <Select
              value={String(op.casasDecimaisValorUnit)}
              onValueChange={(v) => set('casasDecimaisValorUnit', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} casas</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Casas decimais — Quantidade</Label>
            <Select
              value={String(op.casasDecimaisQtde)}
              onValueChange={(v) => set('casasDecimaisQtde', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2, 3, 4].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} casas</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Versão IBPT</Label>
            <Input
              value={op.versaoIbpt ?? ''}
              onChange={(e) => set('versaoIbpt', e.target.value || null)}
              placeholder="24.1.A"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Página
// ══════════════════════════════════════════════════════════════════════════════

const ParametrosSistema = () => {
  const { user } = useAuth();
  const empresaId = user?.empresa.id;

  const { data, isLoading, salvar, isSalvando } = useConfigSistema(empresaId);

  const [smtp,  setSmtp]  = useState<ConfigSmtp>(defaultSmtp);
  const [flags, setFlags] = useState<ConfigFlagsFiscais>(defaultFlags);
  const [op,    setOp]    = useState<ConfigOperacional>(defaultOperacional);

  useEffect(() => {
    if (!data) return;
    setSmtp(data.smtp);
    setFlags(data.flagsFiscais);
    setOp(data.operacional);
  }, [data]);

  const handleSalvar = () => {
    const req: AtualizarConfigSistemaRequest = {
      smtp,
      flagsFiscais: flags,
      operacional: op,
    };
    salvar(req);
  };

  const SaveButton = (
    <div className="flex justify-end pt-4">
      <Button onClick={handleSalvar} disabled={isSalvando} className="gap-2 w-full sm:w-auto">
        {isSalvando
          ? <><RefreshCw className="h-4 w-4 animate-spin" />Salvando...</>
          : <><Save className="h-4 w-4" />Salvar Parâmetros</>}
      </Button>
    </div>
  );

  if (isLoading)
    return (
      <div className="p-6 space-y-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Parâmetros do Sistema</h1>
        <p className="text-muted-foreground mt-1">
          Configurações gerais de e-mail, documentos fiscais e regras operacionais.
        </p>
      </div>

      <Tabs defaultValue="smtp">
        <TabsList>
          <TabsTrigger value="smtp" className="gap-2">
            <Mail className="h-4 w-4" />
            SMTP / E-mail
          </TabsTrigger>
          <TabsTrigger value="flags" className="gap-2">
            <Flag className="h-4 w-4" />
            Documentos Fiscais
          </TabsTrigger>
          <TabsTrigger value="operacional" className="gap-2">
            <Settings2 className="h-4 w-4" />
            Operacional
          </TabsTrigger>
        </TabsList>

        <TabsContent value="smtp">
          <TabSmtp smtp={smtp} onChange={setSmtp} />
          {SaveButton}
        </TabsContent>

        <TabsContent value="flags">
          <TabFlags flags={flags} onChange={setFlags} />
          {SaveButton}
        </TabsContent>

        <TabsContent value="operacional">
          <TabOperacional op={op} onChange={setOp} />
          {SaveButton}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ParametrosSistema;
